// components/patient/FileUpload.tsx
import React, { useState, useRef, useCallback } from 'react';
import { 
  PhotoIcon, 
  DocumentTextIcon, 
  XMarkIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowUpTrayIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { patientApiUtils } from '../../services/patientApi';

interface FileUploadProps {
  onFileSelect: (file: File) => void;
  onFileRemove: (index: number) => void;
  attachedFiles: File[];
  maxFiles?: number;
  maxSizePerFile?: number; // in bytes
  acceptedTypes?: string[];
  disabled?: boolean;
}

interface FilePreview {
  file: File;
  url?: string;
  error?: string;
  uploading?: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  onFileRemove,
  attachedFiles = [],
  maxFiles = 5,
  maxSizePerFile = 10 * 1024 * 1024, // 10MB
  acceptedTypes = ['image/*', '.pdf', '.doc', '.docx', '.txt'],
  disabled = false,
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [previews, setPreviews] = useState<FilePreview[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Generate file previews
  const generatePreview = useCallback((file: File): FilePreview => {
    const preview: FilePreview = { file };

    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      preview.url = url;
    }

    // Validate file
    const validation = patientApiUtils.validateFile(file);
    if (!validation.valid) {
      preview.error = validation.error;
    }

    return preview;
  }, []);

  // Update previews when attachedFiles changes
  React.useEffect(() => {
    const newPreviews = attachedFiles.map(generatePreview);
    setPreviews(newPreviews);

    // Cleanup old URLs
    return () => {
      previews.forEach(preview => {
        if (preview.url) {
          URL.revokeObjectURL(preview.url);
        }
      });
    };
  }, [attachedFiles, generatePreview]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    
    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      handleFiles(files);
      // Reset input value to allow selecting the same file again
      e.target.value = '';
    }
  };

  const handleFiles = (files: File[]) => {
    if (attachedFiles.length + files.length > maxFiles) {
      alert(`You can only upload up to ${maxFiles} files.`);
      return;
    }

    files.forEach((file, index) => {
      const validation = patientApiUtils.validateFile(file);
      
      if (validation.valid) {
        onFileSelect(file);
      } else {
        alert(`File "${file.name}" is invalid: ${validation.error}`);
      }
    });
  };

  const openFileDialog = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const removeFile = (index: number) => {
    if (previews[index]?.url) {
      URL.revokeObjectURL(previews[index].url!);
    }
    onFileRemove(index);
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) {
      return <PhotoIcon className="h-8 w-8 text-blue-500" />;
    }
    return <DocumentTextIcon className="h-8 w-8 text-gray-800" />;
  };

  const getFileSizeColor = (size: number) => {
    const percentage = (size / maxSizePerFile) * 100;
    if (percentage >= 90) return 'text-red-600';
    if (percentage >= 75) return 'text-yellow-600';
    return 'text-gray-900';
  };

  const canUploadMore = attachedFiles.length < maxFiles;

  return (
    <div className="space-y-4">
      {/* Upload area */}
      {canUploadMore && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={openFileDialog}
          className={`
            relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
            ${disabled 
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed' 
              : dragOver 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
            }
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={acceptedTypes.join(',')}
            onChange={handleFileInputChange}
            disabled={disabled}
            className="hidden"
          />

          <div className="space-y-3">
            <div className="flex justify-center">
              <ArrowUpTrayIcon 
                className={`h-12 w-12 ${
                  disabled ? 'text-gray-300' : 'text-gray-700'
                }`} 
              />
            </div>

            <div>
              <p className={`text-lg font-medium ${
                disabled ? 'text-gray-700' : 'text-gray-700'
              }`}>
                Drop files here or click to browse
              </p>
              <p className={`text-sm mt-2 ${
                disabled ? 'text-gray-300' : 'text-gray-800'
              }`}>
                {acceptedTypes.join(', ')} up to {patientApiUtils.formatFileSize(maxSizePerFile)} each
              </p>
              <p className={`text-xs mt-1 ${
                disabled ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Maximum {maxFiles} files total
              </p>
            </div>
          </div>

          {/* Drag overlay */}
          {dragOver && (
            <div className="absolute inset-0 bg-blue-100 bg-opacity-75 border-2 border-blue-500 rounded-lg flex items-center justify-center">
              <p className="text-blue-700 font-medium">Drop files to upload</p>
            </div>
          )}
        </div>
      )}

      {/* File list */}
      {previews.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700">
            Attached Files ({previews.length}/{maxFiles})
          </h4>

          {previews.map((preview, index) => (
            <div
              key={index}
              className={`
                flex items-center p-4 border rounded-lg
                ${preview.error 
                  ? 'border-red-200 bg-red-50' 
                  : 'border-gray-200 bg-white'
                }
              `}
            >
              {/* File icon/preview */}
              <div className="flex-shrink-0 mr-4">
                {preview.url ? (
                  <div className="relative group">
                    <img
                      src={preview.url}
                      alt={preview.file.name}
                      className="h-16 w-16 object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-lg flex items-center justify-center transition-all">
                      <EyeIcon className="h-6 w-6 text-white opacity-0 group-hover:opacity-100" />
                    </div>
                  </div>
                ) : (
                  <div className="h-16 w-16 bg-gray-100 rounded-lg flex items-center justify-center">
                    {getFileIcon(preview.file)}
                  </div>
                )}
              </div>

              {/* File info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {preview.file.name}
                </p>
                <p className={`text-sm ${getFileSizeColor(preview.file.size)}`}>
                  {patientApiUtils.formatFileSize(preview.file.size)}
                </p>
                
                {preview.error && (
                  <p className="text-sm text-red-600 flex items-center mt-1">
                    <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                    {preview.error}
                  </p>
                )}

                {preview.uploading && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-xs text-gray-800">
                      <span>Uploading...</span>
                      <span>{uploadProgress[preview.file.name] || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                      <div
                        className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress[preview.file.name] || 0}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex-shrink-0 ml-4">
                {!preview.uploading && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                    className="p-2 text-gray-700 hover:text-red-600 rounded-full hover:bg-gray-100"
                    title="Remove file"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* File type help */}
      {attachedFiles.length === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h5 className="text-sm font-medium text-blue-900 mb-2 flex items-center">
            <PhotoIcon className="h-4 w-4 mr-1" />
            What can you upload?
          </h5>
          <div className="text-sm text-blue-800 space-y-1">
            <p><strong>Images:</strong> Photos, screenshots, or medical images (JPG, PNG, GIF)</p>
            <p><strong>Documents:</strong> Forms, reports, or notes (PDF, DOC, DOCX, TXT)</p>
            <p><strong>Size limit:</strong> Up to {patientApiUtils.formatFileSize(maxSizePerFile)} per file</p>
            <p><strong>Total limit:</strong> Maximum {maxFiles} files</p>
          </div>
          
          <div className="mt-3 p-2 bg-blue-100 rounded text-xs text-blue-800">
            <strong>Privacy Note:</strong> All files are encrypted and stored securely. 
            Only authorized medical staff will have access to your uploads.
          </div>
        </div>
      )}

      {/* Upload limit reached */}
      {!canUploadMore && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2" />
            <p className="text-sm text-yellow-800">
              You've reached the maximum of {maxFiles} files. Remove a file to add another.
            </p>
          </div>
        </div>
      )}

      {/* Security notice */}
      <div className="text-xs text-gray-800 bg-gray-50 p-3 rounded-lg">
        <div className="flex items-start">
          <CheckCircleIcon className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-gray-700">Secure Upload</p>
            <p>Files are automatically scanned for viruses and encrypted during transmission and storage.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUpload;